import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import LiveLayout from "./LiveLayout";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import { Shield, GitCompareArrows, Search, X, ArrowRight } from "lucide-react";

// [key, label, higherBetter, formatter]
const ROWS = [
  ["cbr_team_rating", "CBR Team Rating™", true],
  ["national_rank", "National Rank", false, (v) => (v == null ? "—" : `#${v}`)],
  ["provincial_rank", "Provincial Rank", false, (v) => (v == null ? "—" : `#${v}`)],
  ["strength_of_schedule", "Strength of Schedule", true, (v) => (v == null ? "—" : v)],
  ["overall_record", "Record", null],
  ["tournament_record", "Tournament Results", null],
  ["avg_player_rating", "Avg Player Rating", true],
  ["team_avg", "Team Batting Avg", true],
  ["ops", "OPS", true],
  ["era", "ERA", false],
  ["whip", "WHIP", false],
  ["commitments", "College Commitments", true],
  ["top100", "Top 100 Players", true],
  ["championships", "Championships", true],
  ["roster_size", "Roster Size", null],
];

export default function CompareTeams() {
  const [sp, setSp] = useSearchParams();
  const [teams, setTeams] = useState([]);
  const [a, setA] = useState(sp.get("a") || "");
  const [b, setB] = useState(sp.get("b") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/live/teams").then(({ data }) => setTeams(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (a && b && a !== b) {
      setLoading(true);
      api.get("/live/teams-compare", { params: { a, b } })
        .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
      setSp({ a, b });
    } else { setData(null); }
  }, [a, b]);

  const [ta, tb] = data?.teams || [null, null];

  return (
    <LiveLayout>
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-4xl mx-auto px-5 py-7">
          <div className="label-overline text-[#E63946] flex items-center gap-2"><GitCompareArrows className="w-4 h-4" /> Compare Teams</div>
          <h1 className="font-display text-3xl uppercase tracking-tight mt-1">Head to Head</h1>
          <p className="text-sm text-white/60 mt-1">Pick two teams to compare ratings, records, stats and recruiting side by side.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-6" data-testid="compare-teams">
        <div className="grid grid-cols-2 gap-3 mb-6">
          <TeamPicker teams={teams} value={a} onChange={setA} exclude={b} testid="compare-picker-a" />
          <TeamPicker teams={teams} value={b} onChange={setB} exclude={a} testid="compare-picker-b" />
        </div>

        {loading ? <div className="py-16 text-center text-sm text-slate-400">Comparing…</div>
          : !ta || !tb ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-lg py-16 text-center text-sm text-slate-400" data-testid="compare-empty">
              Select two teams above to see a full comparison.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="compare-table">
              {/* Header row */}
              <div className="grid grid-cols-3 bg-[#0A192F] text-white">
                <TeamHead t={ta} />
                <div className="grid place-items-center text-[10px] uppercase tracking-widest text-white/40 font-semibold">vs</div>
                <TeamHead t={tb} />
              </div>
              {/* Stat rows */}
              {ROWS.map(([key, label, hb, fmt]) => (
                <CompareRow key={key} label={label} left={ta[key]} right={tb[key]} higherBetter={hb} fmt={fmt} />
              ))}
              {/* Recent form */}
              <div className="grid grid-cols-3 items-center border-t border-slate-100 py-3 px-2">
                <FormCells form={ta.recent_form} align="justify-start" />
                <div className="text-center text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Recent Form (10)</div>
                <FormCells form={tb.recent_form} align="justify-end" />
              </div>
            </div>
          )}
      </div>
    </LiveLayout>
  );
}

function TeamPicker({ teams, value, onChange, exclude, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = teams.find((t) => t.id === value);
  const filtered = teams.filter((t) => t.id !== exclude && t.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative">
      <button data-testid={testid} onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-3 hover:border-[#E63946] text-left">
        <EntityLogo src={selected?.effective_logo_url || selected?.logo_url} alt={selected?.name} size="w-8 h-8" textSize="text-[8px]" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{selected ? selected.name : "Select a team"}</div>
          {selected && <div className="text-[11px] text-slate-400">{[selected.age_division, selected.province].filter(Boolean).join(" · ")}</div>}
        </div>
        {selected ? <X className="w-4 h-4 text-slate-300" onClick={(e) => { e.stopPropagation(); onChange(""); }} /> : <Search className="w-4 h-4 text-slate-300" />}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-2">
          <input autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full border border-slate-200 rounded-sm px-2 py-1.5 text-sm mb-2 outline-none" />
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {filtered.map((t) => (
              <button key={t.id} data-testid={`${testid}-option-${t.id}`} onClick={() => { onChange(t.id); setOpen(false); setQ(""); }}
                className="w-full flex items-center gap-2 p-2 rounded-sm hover:bg-slate-50 text-left">
                <div className="w-6 h-6 rounded-sm bg-slate-100 grid place-items-center text-[9px] text-slate-400 shrink-0">{t.name[0]}</div>
                <span className="text-sm truncate">{t.name}</span>
              </button>
            ))}
            {filtered.length === 0 && <div className="text-xs text-slate-400 py-3 text-center">No teams.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
function TeamHead({ t }) {
  return (
    <Link to={`/live/teams/${t.slug}/portfolio`} className="p-4 flex flex-col items-center text-center gap-2 hover:bg-white/5">
      <EntityLogo src={t.effective_logo_url || t.logo_url} alt={t.name} size="w-12 h-12" textSize="text-sm" variant="dark" />
      <div className="text-sm font-bold uppercase tracking-tight leading-tight">{t.name}</div>
      <div className="text-[10px] text-white/50">{[t.age_division, t.province].filter(Boolean).join(" · ")}</div>
    </Link>
  );
}
function CompareRow({ label, left, right, higherBetter, fmt }) {
  const f = fmt || ((v) => (v == null ? "—" : v));
  let lWin = false, rWin = false;
  if (higherBetter != null && left != null && right != null && !isNaN(Number(left)) && !isNaN(Number(right)) && Number(left) !== Number(right)) {
    const leftBigger = Number(left) > Number(right);
    lWin = higherBetter ? leftBigger : !leftBigger;
    rWin = !lWin;
  }
  return (
    <div className="grid grid-cols-3 items-center border-t border-slate-100 text-sm">
      <div className={`py-2.5 px-3 text-right font-data ${lWin ? "text-[#E63946] font-bold" : "text-slate-700"}`}>{f(left)}</div>
      <div className="py-2.5 px-2 text-center text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className={`py-2.5 px-3 text-left font-data ${rWin ? "text-[#E63946] font-bold" : "text-slate-700"}`}>{f(right)}</div>
    </div>
  );
}
function FormCells({ form = [], align }) {
  const map = { W: "bg-emerald-500", L: "bg-[#E63946]", "–": "bg-slate-200" };
  return (
    <div className={`flex gap-1 px-3 flex-wrap ${align}`}>
      {form.length === 0 ? <span className="text-xs text-slate-400">No games</span>
        : form.map((r, i) => <span key={i} className={`w-5 h-5 rounded-sm grid place-items-center text-[10px] font-bold text-white ${map[r] || "bg-slate-300"}`}>{r}</span>)}
    </div>
  );
}
